import { useCallback, useMemo, useRef, useState } from "react";

import { TableColumnSettingsProvider } from "@providers/TableColumnSettingsProvider/TableColumnSettingsProvider";

import {
  NotificationFilterForm,
  NotificationFilterFormRef,
  NotificationFilterFormValues,
} from "@components/Forms";
import {
  NotificationsTable,
  NotificationsViewMode,
} from "@components/Tables";
import {
  NotificationsHistoryTableConfig,
  NotificationsTableConfig,
  areFiltersActive,
  getDefaultFilterValues,
} from "@components/Tables/NotificationsTable/NotificationsTableConfig";
import { NotificationsTableColumnCustomization } from "@components/Tables/NotificationsTable/UI/NotificationsTableColumnCustomization";
import {
  DEFAULT_ACTIVE_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_HISTORY_NOTIFICATION_TABLE_COLUMN_SETTINGS,
  DEFAULT_NOTIFICATION_TABLE_COLUMN_ORDER,
} from "@components/Tables/NotificationsTable/UI/NotificationsTableColumns";

import { Box } from "@design-system/Box/Box";
import { IconButton } from "@design-system/IconButton/IconButton";
import { SlideInPanel } from "@design-system/SlideInPanel";
import { Text } from "@design-system/Text/Text";

import { PortalHeaderActions } from "@layouts/PortalPage/PortalHeaderActions";
import { NotificationDetailsPanel } from "@components/Panels/NotificationDetailsPanel";

type Props = {
  viewMode?: NotificationsViewMode;
  initialPageIndex?: number;
};

export const NotificationsCard = ({
  viewMode = "active",
  initialPageIndex = 0,
}: Props) => {
  const isHistoryView = viewMode === "history";
  const tableConfig = isHistoryView
    ? NotificationsHistoryTableConfig
    : NotificationsTableConfig;
  const defaultColumnSettings = isHistoryView
    ? DEFAULT_HISTORY_NOTIFICATION_TABLE_COLUMN_SETTINGS
    : DEFAULT_ACTIVE_NOTIFICATION_TABLE_COLUMN_SETTINGS;

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isColumnSettingsOpen, setIsColumnSettingsOpen] = useState(false);
  const [filterValues, setFilterValues] =
    useState<NotificationFilterFormValues>(getDefaultFilterValues());

  const filterFormRef = useRef<NotificationFilterFormRef>(null);

  const filtersActive = useMemo(() => {
    return areFiltersActive(filterValues, isHistoryView);
  }, [filterValues, isHistoryView]);

  const handleFilterClick = useCallback(() => {
    setIsFilterPanelOpen(true);
  }, []);

  const handleCloseFilterPanel = useCallback(() => {
    setIsFilterPanelOpen(false);
  }, []);

  const handleFiltersReset = useCallback(() => {
    setFilterValues(getDefaultFilterValues());
  }, []);

  const handleFilterChange = useCallback(
    (newValues: NotificationFilterFormValues) => {
      setFilterValues(newValues);
      setIsFilterPanelOpen(false);
    },
    [],
  );

  const handleApplyFilters = useCallback(() => {
    if (filterFormRef.current) {
      const currentFormValues = filterFormRef.current.getCurrentValues();
      setFilterValues(currentFormValues);
      setIsFilterPanelOpen(false);
    }
  }, []);

  const handleColumnSettingsClick = useCallback(() => {
    setIsColumnSettingsOpen(true);
  }, []);

  const handleCloseColumnSettings = useCallback(() => {
    setIsColumnSettingsOpen(false);
  }, []);

  return (
    <TableColumnSettingsProvider
      tableId={tableConfig.tableId}
      defaultColumnSettings={defaultColumnSettings}
      defaultColumnOrder={DEFAULT_NOTIFICATION_TABLE_COLUMN_ORDER}
    >
      <PortalHeaderActions>
        <Box direction="row" gap={8} align="center">
          {filtersActive && (
            <Text size={12} color={"textSecondary"}>
              Filters are applied
            </Text>
          )}
          <IconButton
            size="sm"
            icon="faFilter"
            onClick={handleFilterClick}
            outline={"sm"}
            showBubble={filtersActive}
            tooltip="Show filters"
          />
          <IconButton
            size="sm"
            icon="faTableColumns"
            onClick={handleColumnSettingsClick}
            outline={"sm"}
            tooltip="Show/Hide columns"
          />
        </Box>
      </PortalHeaderActions>
      <NotificationsTable
        initialPageIndex={initialPageIndex}
        viewMode={viewMode}
        {...(filterValues.clientId && {
          clientId: filterValues.clientId,
        })}
        {...(filterValues.notificationType && {
          notificationType: filterValues.notificationType,
        })}
        {...(filterValues.applicationNameSearch && {
          applicationNameSearch: filterValues.applicationNameSearch,
        })}
        {...(isHistoryView &&
          filterValues.resolvedById && {
            resolvedById: filterValues.resolvedById,
          })}
        {...(isHistoryView &&
          filterValues.resolvedAt_Gte && {
            resolvedAt_Gte: filterValues.resolvedAt_Gte,
          })}
        {...(isHistoryView &&
          filterValues.resolvedAt_Lte && {
            resolvedAt_Lte: filterValues.resolvedAt_Lte,
          })}
      />

      <SlideInPanel
        isOpen={isFilterPanelOpen}
        onClose={handleCloseFilterPanel}
        onCancel={handleFiltersReset}
        onApply={handleApplyFilters}
        cancelButtonText={filtersActive ? "Clear filters" : undefined}
        applyButtonText="Apply filters"
        title={
          isHistoryView ? "Notification History Filters" : "Notification Filters"
        }
      >
        <NotificationFilterForm
          ref={filterFormRef}
          onFilterChange={handleFilterChange}
          values={filterValues}
          errorsAPI={undefined}
          showHistoryFilters={isHistoryView}
        />
      </SlideInPanel>

      <NotificationsTableColumnCustomization
        isOpen={isColumnSettingsOpen}
        onClose={handleCloseColumnSettings}
        defaultColumnSettings={defaultColumnSettings}
      />
      <NotificationDetailsPanel />
    </TableColumnSettingsProvider>
  );
};
